import { RefObject } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateTime } from "@/Common/Helpers/DateTime";

interface IDateSelectorProps {
    id: string;
    cssClass?: string;
    containerClassName?: string;
    date: Date;
    dataCy?: string;
    onSelectOp?: number;
    dateFormat: string;
    disabled?: boolean;
    valid?: boolean;

    /**
     * A callback invoked when the selected date changes, in cases where you don't need to use
     * {@link onSelectOp}.
     */
    onChangeCallback?: (date: Date) => void;
    onBlur?: () => void;
    toDayEnabled?: boolean;
    todayOperation?: number;
    minDate?: Date;
    maxDate?: Date;
    isMonthPicker?: boolean;
    constainerStyle?: React.CSSProperties;
    isFromMedfarBilling?: boolean;
    width?: number | string;
    height?: number;
    focus?: boolean;
    autoComplete?: "off";
    ignoreInvalidCharacters?: boolean;
    placeholder?: string;
    onClear?: () => void;
}

class DateSelectorState {
    /**
     * Whether the input box should be focused.
     */
    public focus: boolean;
}

class DateSelector extends BaseReactComponent<IDateSelectorProps, DateSelectorState> {
    private culture: string;
    private textInputRef: RefObject<HTMLInputElement> = React.createRef();

    private get textInput(): HTMLInputElement {
        return this.textInputRef.current;
    }

    constructor(props: IDateSelectorProps) {
        super(props);

        this.culture = sessionStore.getUserLanguageCodeFull();

        this.state = {
            focus: this.props.focus
        };
    }

    public componentDidMount(): void {
        const input = $(this.textInput);

        const kendoOptions: kendo.ui.DatePickerOptions = {
            culture: this.culture,
            format: this.props.dateFormat,
            min: this.props.minDate,
            max: this.props.maxDate,
            value: this.props.date,
            parseFormats: ["yyyy-MM-dd", "yyyy/MM/dd", "yyyyMMdd"],
            footer: null,
            open: () => window.setTimeout(() => $(`#${this.props.id}_dateview`).parent().css("height", "265px"), 40),
            change: e => {
                const date: Date = e.sender.value();
                if (this.props.onSelectOp != null)
                    sessionStore.dispatch(this.props.onSelectOp, { value: date });
                this.props.onChangeCallback?.(date);
            }
        };

        if (this.props.isMonthPicker) {
            kendoOptions.start = "year";
            kendoOptions.depth = "year";
        }

        input.kendoDatePicker(kendoOptions);
        input.data("kendoDatePicker").enable(!this.props.disabled);

        const height = this.props.height || 33;

        if (typeof this.props.width === "string") {
            input
                .parent()
                .css("width", this.props.width)
                .css("height", height + "px")
                .parent()
                .css("width", this.props.width);
        }
        else if (typeof this.props.width === "number" && this.props.width >= 0) {
            input
                .parent()
                .css("width", (this.props.width - 30).toString() + "px")
                .css("height", height + "px")
                .parent()
                .css("width", this.props.width.toString() + "px");
        }

        if (this.props.onBlur) {
            input.blur(() => this.props.onBlur());
        }

        if (this.props.onBlur) {
            input.blur(() => this.props.onBlur());
        }
    }

    public shouldComponentUpdate(nextProps: any): boolean {
        return nextProps.disabled != this.props.disabled
            || nextProps.valid != this.props.valid
            || nextProps.date != this.props.date;
    }

    public componentDidUpdate(): void {
        const input = $(this.textInput);

        input.data("kendoDatePicker")?.value(kendo.toString(this.props.date, this.props.dateFormat, this.culture));
        input.data("kendoDatePicker")?.enable(!this.props.disabled);

        if (this.state.focus)
            input.focus();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        const input = $(this.textInput);

        try {
            input.data("kendoDatePicker").destroy();
        }
        catch (e) { }
    }

    public componentWillReceiveProps(nextProps: IDateSelectorProps): void {
        if (this.props.focus !== nextProps.focus)
            this.mutateState(s => s.focus = nextProps.focus);

        if (this.props.maxDate !== nextProps.maxDate) {
            const input = $(this.textInput);

            input.data("kendoDatePicker").setOptions({ ...input.data("kendoDatePicker").options, max: nextProps.maxDate });
        }

        if (this.props.minDate !== nextProps.minDate) {
            const input = $(this.textInput);

            input.data("kendoDatePicker").setOptions({ ...input.data("kendoDatePicker").options, min: nextProps.minDate });
        }
    }

    public render(): JSX.Element {
        const css = this.props.cssClass ?? "";
        const containerClassName = this.props.containerClassName ?? "";

        if (!this.props.isFromMedfarBilling) {
            return (
                <div className={`kendo-ctrl ${this.props.valid === false ? "has-error" : ""} ${containerClassName}`} style={this.props.constainerStyle}>
                    <input
                        ref={this.textInputRef}
                        type="text"
                        disabled={this.props.disabled}
                        id={this.props.id}
                        data-cy={this.props.dataCy}
                        key={this.props.id}
                        className={css}
                        autoComplete={this.props.autoComplete}
                        onBlur={() => this.onBlur()}
                        onChange={() => this.onChange()}
                        placeholder={this.props.placeholder}
                    />
                    {(this.props.toDayEnabled && !this.props.disabled) && (
                        <span
                            style={{ margin: "10px" }}
                        >
                            <a onClick={e => this.onTodayClick(e)} data-cy={this.props.dataCy ? `${this.props.dataCy}-today` : null}>
                                {cacheStore.GetString("TodayLabel")}
                            </a>
                        </span>
                    )}
                </div>
            );
        }
        else {
            return (
                <div className={`flex-container ${this.props.valid === false ? " invalid" : ""} ${containerClassName}`} style={this.props.constainerStyle}>
                    <input
                        ref={this.textInputRef}
                        type="text"
                        disabled={this.props.disabled}
                        id={this.props.id}
                        key={this.props.id}
                        autoComplete={this.props.autoComplete}
                        className={css}
                        onBlur={() => this.onBlur()}
                        onChange={() => this.onChange()}
                    />
                    {(this.props.toDayEnabled && !this.props.disabled) && (
                        <span>
                            <a onClick={e => this.onTodayClick(e)}>
                                {cacheStore.GetString("TodayLabel")}
                            </a>
                        </span>
                    )}
                </div>
            );
        }
    }

    public open(): void {
        $(this.textInput).data("kendoDatePicker").open();
    }

    private onTodayClick(e: React.MouseEvent): void {
        e.preventDefault();

        this.props.onChangeCallback?.(DateTime.nowDeprecated());
        if (this.props.todayOperation != null)
            sessionStore.dispatch(this.props.todayOperation);
    }

    private onBlur(): void {
        this.mutateState(s => s.focus = false);
    }

    private onChange(): void {
        if (this.props.onClear && !this.textInput.value) {
            this.props.onClear();
        }
        else if (this.props.ignoreInvalidCharacters) {
            this.mutateState(() => {
                const value = this.textInput.value;
                const invalidChars = /[^\/\-\d]/gi;
                let cursorPosition = this.textInput.selectionStart;

                let beforeCursor = value.substring(0, cursorPosition);
                let afterCursor = value.substring(cursorPosition, value.length);
                const initialBeforeCursorLength = beforeCursor.length;

                beforeCursor = beforeCursor.replace(invalidChars, "");
                afterCursor = afterCursor.replace(invalidChars, "");
                cursorPosition -= initialBeforeCursorLength - beforeCursor.length;

                this.textInput.value = beforeCursor + afterCursor;
                this.textInput.selectionStart = cursorPosition;
                this.textInput.selectionEnd = cursorPosition;
            });
        }
    }
}

export { DateSelector };
